import { HttpRequest } from 'services/_http'
import { APIEndpoints } from 'constants/api-endpoints'
import { ShowItem } from 'interfaces/widget-types'

const MAX_ITEMS = 4
const MIN_EPISODES = 2

/**
 * Check if a show has the minimum required number of episodes
 */
export const checkShowHasMinEpisodes = async (
  showId: string | number,
  minEpisodes: number = MIN_EPISODES
): Promise<boolean> => {
  try {
    const { data } = await HttpRequest({
      url: `${APIEndpoints.Show}/${showId}/content`,
      method: 'GET'
    })

    const episodes = data?.data?.show?.episodes || data?.show?.episodes || []
    return episodes.length >= minEpisodes
  } catch (error) {
    console.error(`Error checking episodes for show ${showId}:`, error)
    return false
  }
}

/**
 * Filter shows to only include those with more than 2 episodes
 */
export const filterShowsByEpisodes = async (shows: any[]): Promise<any[]> => {
  if (!shows || shows.length === 0) return []

  const checkPromises = shows.map(async (show) => {
    const showId = show.show?.id || show.id
    if (!showId) return null

    const hasMinEpisodes = await checkShowHasMinEpisodes(showId, MIN_EPISODES)
    return hasMinEpisodes ? show : null
  })

  const results = await Promise.all(checkPromises)
  return results.filter((show): show is any => show !== null)
}

/**
 * Get episode count from show data
 */
export const getShowEpisodeCount = (show: any): number => {
  if (show?.episodes?.length) return show.episodes.length
  if (show?.show?.episodes?.length) return show.show.episodes.length
  if (show?.episodeCount) return show.episodeCount
  return 0
}

/**
 * Convert show data to ShowItem format
 */
export const convertToShowItem = (show: any): ShowItem => {
  const showId = (show.show?.id || show.id)?.toString() || ''
  return {
    id: showId,
    thumbnail: show.thumbnail || show.show?.thumbnail || '',
    title: show.name || show.show?.name || show.title || '',
    redirection_to: 'SHOW' as any,
    redirection_id: showId,
  }
}

/**
 * Check if a show can be selected (not already selected and under max limit)
 */
export const canSelectShow = (
  showId: string,
  selectedShowIds: string[],
  maxItems: number = MAX_ITEMS
): boolean => {
  if (selectedShowIds.length >= maxItems) return false
  return !selectedShowIds.includes(showId)
}

/**
 * Add a show to the selected list
 */
export const addShowToSelection = (
  show: any,
  selectedShows: ShowItem[],
  maxItems: number = MAX_ITEMS
): ShowItem[] => {
  const showId = String(show.show?.id || show.id)
  
  // Check if already selected
  if (selectedShows.some(item => item.id === showId || item.redirection_id === showId)) {
    return selectedShows
  }

  // Check if at max limit
  if (selectedShows.length >= maxItems) {
    return selectedShows
  }

  const newItem = convertToShowItem(show)
  return [...selectedShows, newItem].slice(0, maxItems)
}

/**
 * Remove a show from the selected list
 */
export const removeShowFromSelection = (
  showId: string,
  selectedShows: ShowItem[]
): ShowItem[] => {
  return selectedShows.filter(
    item => item.id !== showId && item.redirection_id !== showId
  )
}

/**
 * Get show IDs from selected shows
 */
export const getShowIds = (selectedShows: ShowItem[]): string[] => {
  return selectedShows
    .map(item => item.id || item.redirection_id)
    .filter(Boolean)
    .map(String)
}

/**
 * Initialize card details for selected shows
 */
export const initializeCardDetails = async (
  showIds: string[]
): Promise<Record<string, any>> => {
  const details: Record<string, any> = {}

  for (const showId of showIds) {
    try {
      const { data } = await HttpRequest({
        url: `${APIEndpoints.Show}/${showId}/content`,
        method: 'GET'
      })

      const show = data?.data?.show || data?.show
      if (show) {
        const episodes = show.episodes || []
        details[showId] = {
          title: show.name || '',
          shortSummary: show.short_summary || '',
          episodeCount: episodes.length,
          selectedEpisodes: []
        }
      }
    } catch (error) {
      console.error(`Error initializing card details for show ${showId}:`, error)
    }
  }

  return details
}

/**
 * Update card details when shows are selected/deselected
 */
export const updateCardDetailsForShows = (
  selectedShowIds: string[],
  existingCardDetails: Record<string, any>
): Record<string, any> => {
  const updatedDetails: Record<string, any> = {}

  // Keep only card details for selected shows
  selectedShowIds.forEach(showId => {
    if (existingCardDetails[showId]) {
      updatedDetails[showId] = existingCardDetails[showId]
    }
  })

  return updatedDetails
}

/**
 * Check if all selected shows have card details configured
 */
export const validateCardDetails = (
  selectedShowIds: string[],
  cardDetails: Record<string, any>
): { isValid: boolean; missingShows: string[] } => {
  const missingShows: string[] = []

  selectedShowIds.forEach(showId => {
    const detail = cardDetails[showId]
    if (!detail || !detail.selectedEpisodes || detail.selectedEpisodes.length === 0) {
      missingShows.push(showId)
    }
  })

  return {
    isValid: missingShows.length === 0,
    missingShows
  }
}

export { MAX_ITEMS, MIN_EPISODES }
